// Engineer-impersonation grant-token scrubbing (spec 048, FR-035..FR-038, FR-067).
//
// Grant tokens are 64-character lowercase/uppercase hex strings minted
// server-side and attached to impersonated requests via a header. They
// must NEVER reach Better Stack, Statsig, the browser console, or any
// other third-party telemetry sink. Defence in depth: the
// impersonation client owns header attachment, but every telemetry
// transport ALSO scrubs payloads on the way out so a stray token in a
// log message, error context, or analytics property cannot leak.
//
// `scrubGrantTokens` is a recursive deep-clone-safe transformer that
// walks plain strings, arrays, plain objects, and Error instances. It
// intentionally leaves non-plain objects (Date, custom class
// instances, Map, Set, etc.) alone — those carry custom serialization
// semantics and rewriting them risks breaking the consumer's
// expectation. Errors get a special branch because their `message` is
// the most likely place a stray token lands and we still want the
// stack/name preserved.
//
// The regex is intentionally narrow: 64 hex chars with word
// boundaries, which matches the grant-token format
// (`crypto.randomBytes(32).toString('hex')`) and avoids false
// positives on Solana base58 pubkeys, transaction signatures, or
// other long IDs.

const GRANT_TOKEN_REGEX: RegExp = /\b[a-fA-F0-9]{64}\b/g;
const REDACTED: string = '<grant-token-redacted>';

/**
 * Recursively scrubs 64-char hex grant tokens from a value. Returns
 * the same shape with token strings replaced by the redacted marker.
 *
 * - Strings: tokens replaced inline.
 * - Arrays: each element scrubbed recursively.
 * - Plain objects (prototype === Object.prototype): each value scrubbed.
 * - Error instances: `message` rewritten in-place; `name`/`stack`
 *   preserved.
 * - Everything else (numbers, booleans, null, Date, Map, Set, custom
 *   classes): returned untouched.
 */
export function scrubGrantTokens<T>(value: T): T {
  if (typeof value === 'string') {
    // ALLOW-UNKNOWN: type-narrowed at use-site / mock signature passthrough.
    return value.replace(GRANT_TOKEN_REGEX, REDACTED) as unknown as T;
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (value instanceof Error) {
    // Mutate in place so consumers that hold the same reference see
    // the scrubbed message. Stack traces can also contain the token
    // (when it appears in the original error message); rewrite that
    // too if present.
    setOwnField(value, 'message', scrubGrantTokens(value.message));
    if (typeof value.stack === 'string') {
      setOwnField(value, 'stack', value.stack.replace(GRANT_TOKEN_REGEX, REDACTED));
    }
    return value;
  }

  if (Array.isArray(value)) {
    // ALLOW-UNKNOWN: type-narrowed at use-site / mock signature passthrough.
    return value.map((v: unknown): unknown => scrubGrantTokens(v)) as unknown as T;
  }

  if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as object)) {
      out[k] = scrubGrantTokens(v);
    }
    return out as T;
  }

  return value;
}

// `message` is a getter-only accessor on DOMException (AbortError and friends),
// so a plain assignment throws in strict mode — from inside the very logger
// call that was reporting the original error. Defining an own property shadows
// the accessor and behaves like assignment for ordinary Errors.
function setOwnField(err: Error, key: 'message' | 'stack', value: string): void {
  Object.defineProperty(err, key, { value, writable: true, configurable: true, enumerable: false });
}
